
import React, { useEffect, useRef, useState } from "react";
import PhotoConsent from "./PhotoConsent";
import AgreementAcceptance from "./AgreementAcceptance";
import DepositPayment from "./DepositPayment";

import { BOOKING_AGREEMENT_VERSION } from "../data/bookingAgreement";
import { calculateBookingPayment } from "../utils/bookingPricing";

function BookingForm({
  braid,
  selections,
  total,
  canBook = true,
}) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    date: "",
    time: "",
    notes: "",
  });

  const [photoConsent, setPhotoConsent] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [bookingResult, setBookingResult] = useState(null);

  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [turnstileError, setTurnstileError] = useState("");

  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);

  // Load and render Turnstile once when the component mounts.
  useEffect(() => {
    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

    if (!siteKey) {
      setTurnstileError(
        "Security verification is not configured. Please contact us."
      );
      console.error("REACT_APP_TURNSTILE_SITE_KEY is missing.");
      return undefined;
    }

    let cancelled = false;
    let script = null;

    const renderWidget = () => {
      if (
        cancelled ||
        !window.turnstile ||
        !turnstileRef.current ||
        turnstileWidgetId.current !== null
      ) {
        return;
      }

      try {
        turnstileWidgetId.current = window.turnstile.render(
          turnstileRef.current,
          {
            sitekey: siteKey,
            theme: "light",
            size: "flexible",

            callback: (token) => {
              setTurnstileToken(token);
              setTurnstileError("");
            },

            "expired-callback": () => {
              setTurnstileToken("");
            },

            "error-callback": () => {
              setTurnstileToken("");
              setTurnstileError(
                "Security verification encountered an error. Please try again."
              );
            },
          }
        );

        setTurnstileReady(true);
        setTurnstileError("");
      } catch (error) {
        console.error("Turnstile failed to render:", error);

        setTurnstileReady(false);
        setTurnstileError(
          "Unable to load security verification. Please refresh the page."
        );
      }
    };

    const handleScriptError = () => {
      if (cancelled) return;

      setTurnstileReady(false);
      setTurnstileError(
        "Unable to load security verification. Please check your connection and refresh the page."
      );
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      script = document.querySelector(
        'script[src*="challenges.cloudflare.com/turnstile"]'
      );

      if (!script) {
        script = document.createElement("script");
        script.src =
          "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }

      script.addEventListener("load", renderWidget);
      script.addEventListener("error", handleScriptError);

      // Check again in case the script became available while setting
      // up the event listeners.
      if (window.turnstile) {
        renderWidget();
      }
    }

    return () => {
      cancelled = true;

      if (script) {
        script.removeEventListener("load", renderWidget);
        script.removeEventListener("error", handleScriptError);
      }

      if (
        turnstileWidgetId.current !== null &&
        window.turnstile
      ) {
        window.turnstile.remove(turnstileWidgetId.current);
        turnstileWidgetId.current = null;
      }
    };
  }, []);

  const payment = calculateBookingPayment(total);

  const money = (amount) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const resetTurnstile = () => {
    setTurnstileToken("");

    if (
      window.turnstile &&
      turnstileWidgetId.current !== null
    ) {
      try {
        window.turnstile.reset(turnstileWidgetId.current);
      } catch (error) {
        console.error("Unable to reset Turnstile:", error);
      }
    }
  };

  const removeTurnstileWidget = () => {
    if (
      window.turnstile &&
      turnstileWidgetId.current !== null
    ) {
      try {
        window.turnstile.remove(turnstileWidgetId.current);
      } catch (error) {
        console.error("Unable to remove Turnstile widget:", error);
      }

      turnstileWidgetId.current = null;
    }

    setTurnstileToken("");
    setTurnstileReady(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");

    if (!turnstileReady || !turnstileToken) {
      setErrorMessage(
        "Please complete the security verification before booking."
      );
      return;
    }

    if (!canBook) {
      setErrorMessage(
        "Please complete your braid selections before booking."
      );
      return;
    }

    if (!agreementAccepted) {
      setErrorMessage(
        "You must accept the booking agreement before submitting."
      );
      return;
    }

    if (!paymentMethod) {
      setErrorMessage("Please choose Cash App or Zelle.");
      return;
    }

    if (!photoConsent) {
      setErrorMessage(
        "Please select your photo and video consent preference."
      );
      return;
    }

    setSubmitting(true);

    try {
      // The server calculates the official price.
      // Do not send client-calculated prices as authoritative values.

      const bookingRequest = {
        customer: {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
        },

        braid: {
          id: braid.id,
        },

        appointment: {
          requestedDate: formData.date,
          requestedTime: formData.time,
        },

        selections,

        customerNotes: formData.notes.trim(),

        paymentMethod,

        agreementAcceptance: {
          accepted: true,
          agreementVersion: BOOKING_AGREEMENT_VERSION,
        },

        photoConsent: {
          choice: photoConsent,
        },

        smsConsent: {
          accepted: smsConsent,
        },
      };

      const response = await fetch("/api/bookings", {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify({
          ...bookingRequest,
          turnstileToken,
        }),
      });

      // Avoid crashing if the server returns a non-JSON error response.
      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          result.message ||
            "Unable to submit your booking. Please try again."
        );
      }

      if (!result.pricing) {
        throw new Error(
          "The server response was incomplete. Please contact us before making another booking."
        );
      }

      setBookingResult(result);
      setSubmitted(true);

      // The form and its widget will no longer be displayed.
      removeTurnstileWidget();
    } catch (error) {
      console.error("Booking submission failed:", error);

      setErrorMessage(
        error.message || "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);

      // A Turnstile token is single-use. Reset it after every attempt.
      resetTurnstile();
    }
  };

  if (submitted && bookingResult) {
    const confirmedPricing = bookingResult.pricing;

    return (
      <div className="booking-success">
        <h2>Booking Request Received!</h2>

        <p>
          Thank you, {formData.firstName}. Your appointment request for{" "}
          <strong>{braid.name}</strong> has been successfully submitted.
        </p>

        <div className="booking-payment-summary">
          <h3>Booking Confirmation</h3>

          <p>
            Confirmation number:{" "}
            <strong>{bookingResult.confirmationNumber}</strong>
          </p>

          <p>
            Booking status:{" "}
            <strong>{bookingResult.bookingStatus}</strong>
          </p>

          <h3>Your Payment Summary</h3>

          <p>
            Total appointment price:{" "}
            <strong>{money(confirmedPricing.total)}</strong>
          </p>

          <p>
            Deposit required (20%):{" "}
            <strong>{money(confirmedPricing.deposit)}</strong>
          </p>

          <p>
            Remaining balance:{" "}
            <strong>{money(confirmedPricing.balance)}</strong>
          </p>

          <p>
            Selected payment method:{" "}
            <strong>
              {paymentMethod === "cashapp" ? "Cash App" : "Zelle"}
            </strong>
          </p>

          <p>
            Deposit status: <strong>Awaiting verification</strong>
          </p>
        </div>

        <div className="payment-instructions">
          <h3>Next Step: Pay Your Deposit</h3>

          {paymentMethod === "cashapp" ? (
            <p>
              Send {money(confirmedPricing.deposit)} using your verified
              Cash App $Cashtag or payment link.
              Replace this text with your actual payment details.
            </p>
          ) : (
            <p>
              Send {money(confirmedPricing.deposit)} using your verified
              Zelle recipient details.
              Replace this text with your actual payment details.
            </p>
          )}

          <p>
            Your appointment is not confirmed until your deposit
            has been received and verified by OneRoof Beauty.
          </p>

          <p>
            Please keep your payment confirmation for your records.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form className="booking-form" onSubmit={handleSubmit}>
      <h2>Book Your Appointment</h2>

      <input
        type="text"
        name="firstName"
        placeholder="First name"
        value={formData.firstName}
        onChange={handleChange}
        autoComplete="given-name"
        maxLength="60"
        required
      />

      <input
        type="text"
        name="lastName"
        placeholder="Last name"
        value={formData.lastName}
        onChange={handleChange}
        autoComplete="family-name"
        maxLength="60"
        required
      />

      <input
        type="email"
        name="email"
        placeholder="Email address"
        value={formData.email}
        onChange={handleChange}
        autoComplete="email"
        maxLength="254"
        required
      />

      <input
        type="tel"
        name="phone"
        placeholder="Phone number"
        value={formData.phone}
        onChange={handleChange}
        autoComplete="tel"
        maxLength="20"
        required
      />

      <label>
        Appointment date
        <input
          type="date"
          name="date"
          value={formData.date}
          onChange={handleChange}
          min={new Date().toLocaleDateString("en-CA")}
          required
        />
      </label>

      <label>
        Appointment time
        <input
          type="time"
          name="time"
          value={formData.time}
          onChange={handleChange}
          required
        />
      </label>

      <textarea
        name="notes"
        placeholder="Additional notes"
        value={formData.notes}
        onChange={handleChange}
        rows="4"
        maxLength="1000"
      />

      <AgreementAcceptance
        accepted={agreementAccepted}
        onAcceptedChange={setAgreementAccepted}
      />

      <PhotoConsent
        value={photoConsent}
        onChange={setPhotoConsent}
      />

      <div className="sms-consent">
        <label>
          <input
            type="checkbox"
            checked={smsConsent}
            onChange={(event) => setSmsConsent(event.target.checked)}
          />

          <span>
            I agree to receive transactional text messages from OneRoof Beauty
            regarding my appointment, including booking confirmations, deposit
            verification, appointment updates, and reminders. Message frequency
            varies. Message and data rates may apply. Reply STOP to opt out.
          </span>
        </label>
      </div>

      <DepositPayment
        totalPrice={total}
        paymentMethod={paymentMethod}
        onPaymentMethodChange={setPaymentMethod}
      />

      <div className="turnstile-container">
        <div ref={turnstileRef}></div>
      </div>

      {turnstileError && (
        <p className="booking-error" role="alert">
          {turnstileError}
        </p>
      )}

      {errorMessage && (
        <p className="booking-error" role="alert">
          {errorMessage}
        </p>
      )}

      <button
        type="submit"
        className="button"
        disabled={
          !canBook ||
          !agreementAccepted ||
          !paymentMethod ||
          !photoConsent ||
          !turnstileReady ||
          !turnstileToken ||
          submitting
        }
      >
        {submitting
          ? "Submitting Booking..."
          : `Request Appointment — ${money(payment.total)}`}
      </button>

      <p className="booking-form-note">
        Your request remains pending until your deposit is verified.
      </p>
    </form>
  );
}

export default BookingForm;
